<script setup>
// Manage a meet's members WITHOUT leaving the page: the roster with per-row
// remove, an Add-members picker (club players, searched client-side, batch
// add in one request), and a create-new-club-member escape hatch. The same
// mechanics as the admin meet page's Members panel — every write returns the
// fresh meet detail, emitted up so the host page swaps it straight in.
import {ref, computed, watch} from 'vue';
// useCrudApi is a Nuxt auto-import (no file under ~/composables) — don't import it.
import {useHttpRequest} from '~/composables/useHttpRequest.js';
import {useLocalisedDates} from '~/composables/useLocalisedDates';
import {endpoints} from '~/network/endpoints.js';
import NewClubMemberDialog from '~/components/admin/NewClubMemberDialog.vue';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  meet: {type: Object, required: true},
});
const emit = defineEmits(['update:modelValue', 'updated']);

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const {httpRequest} = useHttpRequest();
const {shortDate} = useLocalisedDates();

const members = computed(() =>
    [...(props.meet.players || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));

// ── Add members — club-player picker, ticked then added in ONE batch ──
const addDialog = ref(false);
const addSearch = ref('');
const addSelected = ref(new Set());
const addPending = ref(false);
const addErrors = ref({});
const {items: clubPlayers, getItems: getClubPlayers, listPending: playersPending} = useCrudApi('api/v2/players');
const playersLoaded = ref(false);
watch(addDialog, async (isOpen) => {
  if (isOpen && !playersLoaded.value) {
    await getClubPlayers(1, 1000, {clubIds: [props.meet.clubId]});
    playersLoaded.value = true;
  }
});
const memberPlayerIds = computed(() => new Set((props.meet.players || []).map(m => m.playerId)));
const addCandidates = computed(() => {
  const search = addSearch.value.trim().toLowerCase();
  return (clubPlayers.value || [])
      .filter(p => !memberPlayerIds.value.has(p.id))
      .filter(p => !search || (p.fullName || '').toLowerCase().includes(search))
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
      .slice(0, 50);
});
const toggleAdd = (player) => {
  const next = new Set(addSelected.value);
  next.has(player.id) ? next.delete(player.id) : next.add(player.id);
  addSelected.value = next;
};
// A brand-new club member created from inside the picker: reload and pre-tick.
const newMemberDialog = ref(false);
const onNewMember = async (player) => {
  await getClubPlayers(1, 1000, {clubIds: [props.meet.clubId]});
  if (player?.id) addSelected.value = new Set([...addSelected.value, player.id]);
};
const addMembers = async () => {
  if (!addSelected.value.size) return;
  addPending.value = true;
  addErrors.value = {};
  try {
    const fresh = await httpRequest(endpoints.meets.addPlayer(props.meet.id), {playerIds: [...addSelected.value]});
    emit('updated', fresh);
    addDialog.value = false;
    addSelected.value = new Set();
    addSearch.value = '';
  } catch (e) {
    addErrors.value = e && !(typeof e.json === 'function') && Object.keys(e).length ? e : {global: ['Could not add the members']};
  } finally {
    addPending.value = false;
  }
};

// ── Remove — confirm per row ──
const removeDialog = ref(false);
const removeTarget = ref(null);
const removePending = ref(false);
const removeErrors = ref({});
const openRemove = (member) => {
  removeTarget.value = member;
  removeErrors.value = {};
  removeDialog.value = true;
};
const confirmRemove = async () => {
  removePending.value = true;
  try {
    const fresh = await httpRequest(endpoints.meets.removePlayer(props.meet.id, removeTarget.value.playerId));
    emit('updated', fresh);
    removeDialog.value = false;
  } catch (e) {
    removeErrors.value = e && !(typeof e.json === 'function') && Object.keys(e).length ? e : {global: ['Could not remove the member']};
  } finally {
    removePending.value = false;
  }
};
</script>

<template>
  <tl-dialog v-model="open" title="Manage members" :max-width="560">
    <div class="text-caption text-medium-emphasis mb-3">
      {{ meet.name }} · only members can RSVP to its dates.
    </div>

    <div v-if="!members.length" class="text-body-2 text-medium-emphasis text-center py-6">
      No members yet. Add members from the club, or share the meet's join link.
    </div>
    <div v-else class="mm-list">
      <div v-for="member in members" :key="member.id" class="mm-row">
        <avatar-image-loader :image-file-name="member.playerImageFileName" size="34" rounded="circle"/>
        <div class="flex-fill min-w-0">
          <div class="mm-t">{{ member.playerName }}</div>
          <div class="mm-s">Member since {{ shortDate(member.createdAt) }}</div>
        </div>
        <v-btn size="small" variant="text" color="error" @click="openRemove(member)">
          <v-icon size="15" class="mr-1">mdi-account-remove-outline</v-icon>
          Remove
          <v-tooltip activator="parent" location="top" max-width="300">
            Remove this member from the meet. They can no longer RSVP to its dates.
          </v-tooltip>
        </v-btn>
      </div>
    </div>

    <template #actions>
      <v-spacer/>
      <v-btn variant="outlined" @click="open = false">Close</v-btn>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-account-plus-outline" @click="addDialog = true">
        Add members
      </v-btn>
    </template>
  </tl-dialog>

  <!-- Add members — tick as many club members as you like, add in one go -->
  <tl-dialog v-model="addDialog" title="Add members" :max-width="520">
    <v-text-field
        v-model="addSearch"
        placeholder="Search club members"
        prepend-inner-icon="mdi-magnify"
        hide-details
        class="mb-3"
    />
    <v-skeleton-loader v-if="playersPending" type="list-item-avatar-two-line@4"/>
    <template v-else>
      <div v-if="!addCandidates.length" class="text-body-2 text-medium-emphasis text-center py-6">
        No club members match. Everyone may already be a member.
      </div>
      <div
          v-for="player in addCandidates" :key="player.id"
          class="mm-pick"
          :class="{'mm-pick--on': addSelected.has(player.id)}"
          role="checkbox" :aria-checked="addSelected.has(player.id)" tabindex="0"
          @click="toggleAdd(player)"
          @keydown.enter.prevent="toggleAdd(player)"
          @keydown.space.prevent="toggleAdd(player)"
      >
        <avatar-image-loader :image-file-name="player.imageFileName" size="36" rounded="circle"/>
        <div class="flex-fill min-w-0">
          <div class="mm-t">{{ player.fullName }}</div>
          <div class="mm-s">Club member</div>
        </div>
        <v-icon size="22" :color="addSelected.has(player.id) ? 'primary' : 'textSubtle'">
          {{ addSelected.has(player.id) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline' }}
        </v-icon>
      </div>
    </template>
    <crud-error-display class="mt-3" :errors="addErrors"/>
    <template #actions>
      <v-btn
          variant="text" color="primary" prepend-icon="mdi-account-plus"
          :disabled="addPending" @click="newMemberDialog = true"
      >
        New club member
      </v-btn>
      <v-spacer/>
      <v-btn variant="outlined" :disabled="addPending" @click="addDialog = false">Cancel</v-btn>
      <v-btn
          color="primary" variant="flat" prepend-icon="mdi-account-plus-outline"
          :disabled="!addSelected.size" :loading="addPending"
          @click="addMembers"
      >
        Add {{ addSelected.size || '' }} member{{ addSelected.size === 1 ? '' : 's' }}
      </v-btn>
    </template>
  </tl-dialog>

  <!-- New club member from inside Add members — created → reloaded → pre-ticked -->
  <new-club-member-dialog v-model="newMemberDialog" :club-id="meet.clubId" @created="onNewMember"/>

  <!-- Remove member — confirm -->
  <crud-form-dialog
      v-model:dialog="removeDialog"
      v-model="removeTarget"
      action="delete"
      delete-title="Remove member?"
      btn-text="Remove member"
      :form-pending="removePending"
      :save-action="confirmRemove"
  >
    <template #default>
      <p v-if="removeTarget">
        Remove {{ removeTarget.playerName }} from this meet? Their RSVPs for upcoming dates will be cancelled and freed spots offered to the waitlist.
      </p>
      <crud-error-display :errors="removeErrors" show-all/>
    </template>
  </crud-form-dialog>
</template>

<style scoped lang="scss">
.mm-list {
  display: flex;
  flex-direction: column;
}

.mm-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 2px;

  & + .mm-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.mm-t {
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mm-s {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.mm-pick {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  cursor: pointer;

  & + .mm-pick {
    margin-top: 6px;
  }

  &--on {
    border-color: rgb(var(--v-theme-primary));
    background: rgb(var(--v-theme-brandTint));
  }
}
</style>
